import { useState, useCallback, useEffect } from "react";
import { LogIn, LogOut } from "lucide-react";
import { Button, Notice, SkeletonRows } from "../../../components/ui";
import { businessApi } from "../../../services/api";
import { useBusinessAuth } from "../../context/BusinessAuthContext";
import { formatDay } from "../../pages/hr/myHrText";

function formatTimeValue(value) {
  if (!value) return "—";
  const match = /^(\d{2}):(\d{2})/.exec(String(value));
  if (!match) return String(value);
  const hour = Number(match[1]);
  return `${hour % 12 || 12}:${match[2]} ${hour < 12 ? "AM" : "PM"}`;
}

/**
 * Self-service clock-in/out widget for business members.
 * Shows current attendance status and provides clock-in/out buttons.
 *
 * No extra permission (APR-6): the server resolves the caller. Status comes from My HR's own-attendance view.
 */
export default function ClockInWidget({ onUpdated = null, bare = false }) {
  const { currentBusiness } = useBusinessAuth();
  const memberId = currentBusiness?.membership_id;

  const [status, setStatus] = useState(null); // null | 'not_checked_in' | 'checked_in' | 'overdue' | 'checked_out'
  const [checkInTime, setCheckInTime] = useState(null);
  // Report 70 final safeguards: an open clock-in whose scheduled end (and approved overtime) has passed. The server says
  // so (attention missing_clock_out); nothing here counts time or punches anyone out.
  const [overdue, setOverdue] = useState(null); // { date, today, shiftEnd }
  const [checkOutTime, setCheckOutTime] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);
  const [error, setError] = useState("");

  // APR-6: every active employee clocks their own time; the server resolves who you are.
  const canClock = Boolean(memberId);

  const [notice, setNotice] = useState("");

  // Own attendance from My HR (server-resolved member and business date). A clock-in from an
  // earlier date that is still open (an overnight shift) shows Clock Out, not Clock In.
  const fetchToday = useCallback(async () => {
    if (!memberId || !canClock) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError("");
      const res = await businessApi.getMyAttendanceDays();
      const days = res?.days || [];
      const open = [...days].reverse().find((day) => day.state === "in_progress");
      const late = [...days].reverse().find((day) => day.attention === "missing_clock_out"
        && day.record?.check_in && !day.record.check_out && !day.record.clock_out_missing);
      const today = days.find((day) => day.date === res?.today);
      setOverdue(!open && late ? { date: late.date, today: late.date === res?.today, shiftEnd: late.record?.schedule?.endTime || late.schedule?.shift?.endTime || null } : null);
      if (open) {
        setStatus("checked_in");
        setCheckInTime(open.record?.checkInLocal || null);
        setCheckOutTime(null);
      } else if (late && late.date === res?.today) {
        setStatus("overdue");
        setCheckInTime(late.record.checkInLocal || null);
        setCheckOutTime(null);
      } else if (today?.record?.check_in && today.record.check_out) {
        setStatus("checked_out");
        setCheckInTime(today.record.checkInLocal);
        setCheckOutTime(today.record.checkOutLocal);
      } else {
        setStatus("not_checked_in");
        setCheckInTime(null);
        setCheckOutTime(null);
      }
    } catch (err) {
      setStatus("unknown");
      setError("Could not load your attendance. You can still clock in.");
    } finally {
      setLoading(false);
    }
  }, [memberId, canClock]);

  useEffect(() => {
    fetchToday();
  }, [fetchToday]);

  const handleClockIn = async () => {
    if (!memberId) return;
    setActing(true);
    setError("");
    try {
      await businessApi.attendanceCheckIn({ business_member_id: memberId });
      await fetchToday();
      await onUpdated?.();
    } catch (err) {
      const msg = err?.serverMessage || "Clock-in failed. Try again.";
      setError(msg);
    } finally {
      setActing(false);
    }
  };

  const handleClockOut = async () => {
    if (!memberId) return;
    setActing(true);
    setError("");
    try {
      const res = await businessApi.attendanceCheckOut({ business_member_id: memberId });
      if (res?.record?.outcome === "closed_as_missing_clock_out") {
        setNotice("It is too long after your shift ended to clock out now, so the day was sent for review. In My HR, fix the day with the time you left.");
      } else {
        setNotice("");
      }
      await fetchToday();
      await onUpdated?.();
    } catch (err) {
      const msg = err?.serverMessage || "Clock-out failed. Try again.";
      setError(msg);
    } finally {
      setActing(false);
    }
  };

  // Don't render if no permission
  if (!canClock) return null;

  // Presentation on the shared tokens and primitives (UI-3C); what clocking does is unchanged.
  return (
    <div className={bare ? "p-4" : "rounded-panel border border-default bg-surface p-4"}>
      <h3 className="text-panel-title text-foreground">Attendance</h3>

      {loading ? (
        <div className="mt-2"><SkeletonRows rows={1} columns={2} label="Loading your attendance" /></div>
      ) : (
        <div className="mt-2 space-y-3">
          {status === "checked_in" && (
            <p className="text-body-sm text-secondary">
              <span className="font-medium text-foreground">Clocked in</span> at {formatTimeValue(checkInTime)}
            </p>
          )}

          {status === "checked_out" && (
            <p className="text-body-sm text-secondary">
              <span className="font-medium text-foreground">Day complete</span> {formatTimeValue(checkInTime)} — {formatTimeValue(checkOutTime)}
            </p>
          )}

          {status === "overdue" && (
            <p className="text-body-sm text-secondary">
              <span className="font-medium text-foreground">Clocked in</span> at {formatTimeValue(checkInTime)} · no clock-out recorded
            </p>
          )}

          {status === "not_checked_in" && <p className="text-body-sm text-secondary">Not clocked in today</p>}

          {overdue && (
            <Notice tone="warning" role="status">
              {overdue.today
                ? `Your scheduled shift${overdue.shiftEnd ? ` ended at ${formatTimeValue(overdue.shiftEnd)}` : " has ended"} and no clock-out has been recorded. If you're still working, clock out when you actually finish. If you left earlier, don't clock out now: use Fix this day in Attendance and give the time you left.`
                : `Your clock-in on ${formatDay(overdue.date)} has no clock-out. Clocking in today sends that day for review; use Fix this day in Attendance and give the time you left.`}
            </Notice>
          )}

          {notice && <Notice tone="warning" role="status">{notice}</Notice>}

          {error && <p role="alert" className="text-body-sm text-danger">{error}</p>}

          {(status === "not_checked_in" || status === "unknown") && (
            <Button variant="primary" icon={LogIn} loading={acting} onClick={handleClockIn} className="max-sm:w-full">Clock In</Button>
          )}

          {(status === "checked_in" || status === "overdue") && (
            <Button variant="primary" icon={LogOut} loading={acting} onClick={handleClockOut} className="max-sm:w-full">Clock Out</Button>
          )}
        </div>
      )}
    </div>
  );
}
